<template>
  <v-dialog persistent max-width="800px" v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <div class="course-edit-wrapper">
      <v-app-bar app color="white" flat class="app-bar-border">
        <v-btn icon @click="closeDialog()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title v-if="courseEdit" class="font-weight-bold"> Kurs bearbeiten </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="showPushPopupMethod()">
          <v-icon left>mdi-check</v-icon>
          Speichern
        </v-btn>
      </v-app-bar>

      <v-container v-if="!courseEdit" class="d-flex justify-center align-center" style="height: 100vh">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      </v-container>

      <v-container v-else class="form-container">
        <div class="header-image-container">
          <v-img :src="headerImageSrc" class="header-image" height="250px" gradient="to top right, rgba(0,0,0,.3), rgba(25,32,72,.7)">
            <v-row class="fill-height ma-0" align="end" justify="start">
              <v-card-title class="white--text text-h5 font-weight-bold">
                {{ courseEdit.course.name }}
              </v-card-title>
            </v-row>
          </v-img>
        </div>

        <v-row>
          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Grundlagen</v-card-title>
              <v-card-text>
                <course-search :value="courseEdit.course" label="Kursformat" rights="gf_c_plan" @update="updateCourse" outlined></course-search>
                <v-select
                  v-model="courseEdit.label"
                  :items="labels"
                  item-text="text"
                  item-value="_id"
                  label="Label (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-select
                  v-if="events.length"
                  v-model="courseEdit.event"
                  :items="events"
                  item-text="title"
                  item-value="_id"
                  label="Event (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-text-field
                  v-model="courseEdit.additionalInfo"
                  outlined
                  label="Zusatzinfo (optional)"
                  placeholder="z.B. Mindest-Teilnehmerzahl"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Zeit, Ort & Teilnehmer</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="6"><v-text-field v-model="time" type="time" outlined label="Uhrzeit"></v-text-field></v-col>
                  <v-col cols="6"><v-text-field v-model="courseEdit.duration" type="number" outlined label="Dauer (Min)"></v-text-field></v-col>
                </v-row>
                <v-select v-model="courseEdit.room" :items="rooms" item-text="name" item-value="_id" outlined label="Raum"></v-select>
                <v-switch v-model="courseEdit.reqSignup" label="Anmeldung erforderlich" class="mt-4"></v-switch>
                <v-text-field v-if="courseEdit.reqSignup" v-model="courseEdit.amount" type="number" outlined label="Anzahl Plätze"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Kursleiter & Darstellung</v-card-title>
              <v-card-text>
                <div v-for="(instructor, index) in courseEdit.instructors" :key="index" class="d-flex align-center">
                  <user-search
                    :key="index + instructor + '-inst'"
                    instructor
                    :value="instructor"
                    label="Instruktor"
                    rights="gf_c_plan"
                    @update="(newVal) => updateInstructor(newVal, instructor)"
                    outlined
                    class="flex-grow-1"></user-search>
                  <v-btn icon color="grey" @click="removeInstructor(instructor)" class="ml-2"><v-icon>mdi-delete</v-icon></v-btn>
                </div>
                <v-btn text color="primary" @click="addInstructor" class="mt-2"><v-icon left>mdi-plus</v-icon> Instruktor hinzufügen</v-btn>
                <v-divider class="my-6"></v-divider>
                <v-subheader>Kurs-Icon</v-subheader>
                <v-chip-group v-model="courseEdit.icon" column active-class="primary">
                  <v-chip large v-for="ic in icons" :key="ic" :value="ic" filter>
                    <v-avatar class="iconAvatar" center>
                      <lottie-player mode="normal" style="width: 60px; height: 60px" :src="'/api/gf/icons/' + ic"></lottie-player>
                    </v-avatar>
                  </v-chip>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined style="border-color: #d32f2f">
              <v-card-title class="red--text text--darken-2">Kurs-Status</v-card-title>
              <v-card-text>
                <p class="body-1">Sage diesen einzelnen Kurs ab oder reaktiviere ihn wieder. Die Kursplanung bleibt davon unberührt.</p>
                <v-btn
                  block
                  large
                  :dark="!courseEdit.cancelled"
                  :outlined="courseEdit.cancelled"
                  :color="courseEdit.cancelled ? 'grey' : 'red darken-2'"
                  @click="changeCourseActivation">
                  <v-icon left>{{ courseEdit.cancelled ? "mdi-check-circle-outline" : "mdi-cancel" }}</v-icon>
                  {{ courseActivationText }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-dialog v-model="showCancelledPopup" persistent max-width="400px">
        <v-card>
          <v-card-title class="text-h5">Sichtbarkeit des abgesagten Kurses</v-card-title>
          <v-card-text>Soll dieser Kurs für die Kunden in der Kursliste weiterhin sichtbar bleiben (als "abgesagt" markiert)?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cancelledCourseVisible(false)">Nein, ausblenden</v-btn>
            <v-btn color="primary" @click="cancelledCourseVisible(true)">Ja, anzeigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showPushPopUp" persistent max-width="400px">
        <v-card>
          <v-card-title class="text-h5">Teilnehmer benachrichtigen?</v-card-title>
          <v-card-text>Möchtest du die bereits angemeldeten Teilnehmer per Push-Nachricht über die Änderungen informieren?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="saveUpdatedCourse(false)">Nein</v-btn>
            <v-btn color="primary" @click="saveUpdatedCourse(true)">Ja, benachrichtigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import api from "../../api"
import { mapActions } from "vuex"
import userSearch from "../../components/userSearch.vue"
import courseSearch from "../../components/courseSearch.vue"
export default {
  name: "courseListEdit",
  components: { userSearch, courseSearch },
  data() {
    return {
      loading: true,
      dialog: true,
      showCancelledPopup: false,
      showPushPopUp: false,
      courseEdit: null,
      time: "00:00:00",
      rooms: [],
      labels: [{ _id: null, text: "Kein Label" }],
      icons: [],
      events: [], // Beibehalten falls benötigt, im Template aber entfernt
    }
  },
  async created() {
    this.setNavigationBarText(null) // Wir verwenden jetzt die v-app-bar

    try {
      const [dbInfos, iconsData, eventsData] = await Promise.all([
        api.fetchCourseEdit(this.$route.params.id),
        api.fetchAllGFIcons(),
        api.fetchEventsForSelect(),
      ])

      this.rooms = dbInfos.rooms
      this.labels = this.labels.concat(dbInfos.labels)
      this.courseEdit = dbInfos.execution
      this.icons = iconsData.icons
      this.events = eventsData // Events-Daten sind verfügbar

      if (this.events.length) this.events.unshift({ _id: null, title: "Kein Event" })

      // Initialwerte setzen
      this.courseEdit.cancelledVisibility = this.courseEdit.cancelledVisibility || false
      this.courseEdit.room = this.courseEdit.room || null
      this.courseEdit.event = this.courseEdit.event || null
      this.courseEdit.additionalInfo = this.courseEdit.additionalInfo || ""
      this.courseEdit.icon = this.courseEdit.icon || "dumbell.json"

      const hours = String(this.courseEdit.hours).padStart(2, "0")
      const minutes = String(this.courseEdit.minutes).padStart(2, "0")
      this.time = `${hours}:${minutes}`

      this.loading = false
    } catch (error) {
      this.$toast.error("Kurs konnte nicht geladen werden.")
      this.closeDialog()
    }
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText"]),
    showPushPopupMethod() {
      if (this.courseEdit.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      this.showPushPopUp = true
    },
    cancelledCourseVisible(visible) {
      this.courseEdit.cancelledVisibility = visible
      this.showCancelledPopup = false
    },

    async saveUpdatedCourse(sendPush) {
      console.log("Instruktoren", this.courseEdit.instructors)
      this.showPushPopUp = false
      if (this.courseEdit.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      let updatedCourse = JSON.parse(JSON.stringify(this.courseEdit))
      updatedCourse.course = updatedCourse.course._id
      updatedCourse.sendPush = sendPush

      try {
        await api.updateCourseExecution(updatedCourse)
        this.$toast.success("Kurs erfolgreich aktualisiert!")
        this.$emit("updateCourse")
        this.closeDialog()
      } catch (e) {
        this.$toast.error("Speichern fehlgeschlagen.")
      }
    },

    changeCourseActivation() {
      this.courseEdit.cancelled = !this.courseEdit.cancelled
      if (this.courseEdit.cancelled) {
        this.showCancelledPopup = true
      } else {
        this.courseEdit.cancelledVisibility = false
      }
    },

    removeInstructor(id) {
      console.log("Removing instructor with ID:", id)
      this.courseEdit.instructors = this.courseEdit.instructors.filter((instructorId) => instructorId !== id)
    },

    addInstructor() {
      if (!this.courseEdit.instructors.includes(null)) {
        this.courseEdit.instructors.push(null)
      }
    },

    updateCourse(newCourse) {
      this.courseEdit.course = newCourse
    },

    updateInstructor(newId, oldId) {
      if (oldId === null) {
        if (!this.courseEdit.instructors.includes(newId)) {
          this.courseEdit.instructors.pop()
          this.courseEdit.instructors.push(newId)
        } else {
          this.courseEdit.instructors.pop()
        }
      } else {
        const index = this.courseEdit.instructors.indexOf(oldId)
        if (index !== -1) {
          this.courseEdit.instructors.splice(index, 1, newId)
        }
      }
    },

    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.$router.push("/kurse/" + this.courseEdit._id)
      })
    },
  },
  watch: {
    time(val) {
      if (val && this.courseEdit) {
        this.courseEdit.hours = parseInt(val.split(":")[0])
        this.courseEdit.minutes = parseInt(val.split(":")[1])
      }
    },
  },
  computed: {
    courseActivationText() {
      return this.courseEdit.cancelled ? "Kurs reaktivieren" : "Kurs absagen"
    },
    headerImageSrc() {
      return this.courseEdit?.course?.bild ? `/img/kurse/${this.courseEdit.course.bild}` : ""
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .iconAvatar {
  width: 50px !important;
  height: 50px !important;
}
.course-edit-wrapper {
  background-color: #f7f8fa;
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
}

.app-bar-border {
  border-bottom: 1px solid #e0e0e0 !important;
}

.form-container {
  padding-bottom: 24px;
  max-width: 700px;
}

.header-image-container {
  margin: -12px -12px 24px -12px;
  border-radius: 0 0 24px 24px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.form-card {
  border-radius: 16px;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
}

::v-deep .v-chip .v-avatar .lottie-player {
  width: 32px !important;
  height: 32px !important;
}
</style>
